import { BoardLayout } from "@/components/board/BoardLayout";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { Modal } from "@/components/Modal";
import { useActionItemsData, type ActionItem } from "@/hooks/useActionItemsData";
import { useRecentlyChanged } from "@/contexts/RecentlyChangedRowsContext";
import { useQueryParams } from "@/hooks/useQueryParams";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Archive, ChevronDown, ChevronUp, ChevronsUpDown, GripVertical, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

interface ActionRowProps {
  item: ActionItem;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onUpdate: (id: string, updates: Partial<ActionItem>) => void;
  newItemId: string | null;
  isDraggable: boolean;
  readOnly: boolean;
  insertVisible: boolean;
  highlighted: boolean;
  onInsert: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function ActionRow({ item, selected, onToggleSelect, onUpdate, newItemId, isDraggable, readOnly, insertVisible, highlighted, onInsert, onMouseEnter, onMouseLeave }: ActionRowProps) {
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const taskRef = useRef<HTMLTextAreaElement>(null);
  const clientRef = useRef<HTMLTextAreaElement>(null);
  const ownerRef = useRef<HTMLTextAreaElement>(null);

  // Flashes yellow when this row was changed by someone else (another user/tab).
  // markChanged is only set for external edits, so the editor's own row stays calm.
  const isFlashing = useRecentlyChanged("action_items", item.id);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };

  useLayoutEffect(() => {
    if (notesRef.current) {
      notesRef.current.style.height = "auto";
      notesRef.current.style.height = `${notesRef.current.scrollHeight}px`;
    }
  }, [item.notes]);

  useLayoutEffect(() => {
    if (taskRef.current) {
      taskRef.current.style.height = "auto";
      taskRef.current.style.height = `${taskRef.current.scrollHeight}px`;
    }
  }, [item.task]);

  useLayoutEffect(() => {
    if (clientRef.current) {
      clientRef.current.style.height = "auto";
      clientRef.current.style.height = `${clientRef.current.scrollHeight}px`;
    }
  }, [item.client]);

  useLayoutEffect(() => {
    if (ownerRef.current) {
      ownerRef.current.style.height = "auto";
      ownerRef.current.style.height = `${ownerRef.current.scrollHeight}px`;
    }
  }, [item.owner]);

  const save = useCallback(
    (field: keyof ActionItem, value: string | boolean | null) => {
      onUpdate(item.id, { [field]: value });
    },
    [item.id, onUpdate]
  );

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`action-table__row${item.completed ? " action-table__row--completed" : ""}${selected ? " action-table__row--selected" : ""}${highlighted ? " action-table__row--highlight" : ""}${isFlashing ? " action-table__row--flashing" : ""}`}
      data-item-id={item.id}
      data-insert-visible={insertVisible ? "true" : undefined}
      {...attributes}
    >
      <td className="action-table__cell action-table__cell--drag">
        {isDraggable && (
          <button className="action-table__drag-handle" {...listeners} tabIndex={-1} aria-label="Drag to reorder">
            <GripVertical size={14} />
          </button>
        )}
      </td>
      <td className="action-table__cell action-table__cell--select">
        <input
          type="checkbox"
          className="action-table__select-checkbox"
          checked={selected}
          onChange={() => onToggleSelect(item.id)}
        />
      </td>
      <td className="action-table__cell action-table__cell--date">
        <input
          type="date"
          className="action-table__input action-table__input--date"
          defaultValue={item.date ?? ""}
          disabled={readOnly}
          onBlur={(e) => save("date", e.target.value || null)}
        />
      </td>
      <td className="action-table__cell">
        <textarea
          ref={clientRef}
          className="action-table__task-textarea"
          defaultValue={item.client ?? ""}
          placeholder="Client"
          rows={1}
          readOnly={readOnly}
          autoFocus={item.id === newItemId}
          onBlur={(e) => save("client", e.target.value || null)}
          onInput={(e) => autoResize(e.currentTarget)}
        />
      </td>
      <td className="action-table__cell action-table__cell--task">
        <textarea
          ref={taskRef}
          className="action-table__task-textarea"
          defaultValue={item.task}
          placeholder="Task"
          rows={1}
          readOnly={readOnly}
          onBlur={(e) => save("task", e.target.value)}
          onInput={(e) => autoResize(e.currentTarget)}
        />
        {/* Reserves clearance at the row bottom so the insert button never
            overlaps content. Only grows the row when no other column is
            already tall enough to host the button. */}
        <div className="action-table__insert-spacer" aria-hidden="true" />
        {/* Small hover zone at the bottom-center of the row. The button only
            appears while the mouse is near here, not anywhere on the row. */}
        <div
          className="action-table__insert-zone"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {insertVisible && (
            <button
              type="button"
              className="action-table__insert-btn"
              onClick={onInsert}
              aria-label="Insert row below"
            >
              <Plus size={18} />
            </button>
          )}
        </div>
      </td>
      <td className="action-table__cell">
        <textarea
          ref={ownerRef}
          className="action-table__task-textarea"
          defaultValue={item.owner ?? ""}
          placeholder="Owner"
          rows={1}
          readOnly={readOnly}
          onBlur={(e) => save("owner", e.target.value || null)}
          onInput={(e) => autoResize(e.currentTarget)}
        />
      </td>
      <td className="action-table__cell action-table__cell--date">
        <input
          type="date"
          className="action-table__input action-table__input--date"
          defaultValue={item.due_date ?? ""}
          disabled={readOnly}
          onBlur={(e) => save("due_date", e.target.value || null)}
        />
      </td>
      <td className="action-table__cell action-table__cell--completed">
        <input
          type="checkbox"
          className="action-table__completed-checkbox"
          checked={item.completed}
          disabled={readOnly}
          onChange={(e) => save("completed", e.target.checked)}
        />
      </td>
      <td className="action-table__cell action-table__cell--notes">
        <textarea
          ref={notesRef}
          className="action-table__notes"
          defaultValue={item.notes}
          placeholder="Notes"
          rows={1}
          readOnly={readOnly}
          onBlur={(e) => save("notes", e.target.value)}
          onInput={(e) => autoResize(e.currentTarget)}
        />
      </td>
    </tr>
  );
}

type Tab = "active" | "archived";
type SortField = "date" | "due_date";
type SortDir = "asc" | "desc";
type ResizableColumn = "date" | "client" | "task" | "owner" | "dueDate" | "completed" | "notes";

const DEFAULT_COLUMN_WIDTHS: Record<ResizableColumn, number> = {
  date: 11,
  client: 11,
  task: 17,
  owner: 10,
  dueDate: 11,
  completed: 8,
  notes: 26,
};

function compareDate(a: string | null, b: string | null, dir: SortDir) {
  const nullLast = dir === "asc" ? 1 : -1;
  if (a === null && b === null) return 0;
  if (a === null) return nullLast;
  if (b === null) return -nullLast;
  const cmp = a < b ? -1 : a > b ? 1 : 0;
  return dir === "asc" ? cmp : -cmp;
}

export default function ActionItems() {
  const { items, loading, error, createItem, createItemAfter, updateItem, deleteItem, archiveItems, reorderItems } = useActionItemsData();
  const [newItemId, setNewItemId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { get, setParam } = useQueryParams();
  const activeTab: Tab = get("tab") === "archived" ? "archived" : "active";
  const highlightParam = get("item");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightHydratedRef = useRef(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [insertHoverId, setInsertHoverId] = useState<string | null>(null);
  const hideInsertTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);

  const startColumnResize = useCallback(
    (left: ResizableColumn, right: ResizableColumn) =>
      (event: ReactPointerEvent<HTMLSpanElement>) => {
        event.preventDefault();
        const tableWidth = tableWrapperRef.current?.clientWidth ?? 1;
        const startX = event.clientX;
        const startLeft = columnWidths[left];
        const pairTotal = startLeft + columnWidths[right];
        const minLeft = left === "completed" ? 6 : 8;
        const minRight = right === "notes" ? 16 : right === "completed" ? 4 : 8;

        const handleMove = (moveEvent: PointerEvent) => {
          const delta = ((moveEvent.clientX - startX) / tableWidth) * 100;
          const nextLeft = Math.min(
            pairTotal - minRight,
            Math.max(minLeft, startLeft + delta),
          );
          setColumnWidths((current) => ({
            ...current,
            [left]: nextLeft,
            [right]: pairTotal - nextLeft,
          }));
        };

        const handleUp = () => {
          document.removeEventListener("pointermove", handleMove);
          document.removeEventListener("pointerup", handleUp);
          document.body.classList.remove("action-table--resizing");
        };

        document.body.classList.add("action-table--resizing");
        document.addEventListener("pointermove", handleMove);
        document.addEventListener("pointerup", handleUp);
      },
    [columnWidths],
  );

  const colgroup = (
    <colgroup>
      <col className="action-table__col--drag" />
      <col className="action-table__col--select" />
      <col style={{ width: `${columnWidths.date}%` }} />
      <col style={{ width: `${columnWidths.client}%` }} />
      <col style={{ width: `${columnWidths.task}%` }} />
      <col style={{ width: `${columnWidths.owner}%` }} />
      <col style={{ width: `${columnWidths.dueDate}%` }} />
      <col style={{ width: `${columnWidths.completed}%` }} />
      <col style={{ width: `${columnWidths.notes}%` }} />
    </colgroup>
  );

  const resizeHandle = (left: ResizableColumn, right: ResizableColumn) => (
    <span
      className="action-table__resize-handle"
      data-testid={`column-resizer-${left}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${left} column`}
      title="Drag to resize column"
      onPointerDown={startColumnResize(left, right)}
    />
  );

  const showInsertFor = useCallback((id: string) => {
    if (hideInsertTimer.current) clearTimeout(hideInsertTimer.current);
    setInsertHoverId(id);
  }, []);

  const hideInsert = useCallback(() => {
    hideInsertTimer.current = setTimeout(() => setInsertHoverId(null), 100);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filteredItems = useMemo(() => {
    const base = items.filter((i) => (activeTab === "active" ? !i.archived : i.archived));
    if (sortField === null) return base;
    return [...base].sort((a, b) => compareDate(a[sortField], b[sortField], sortDir));
  }, [items, activeTab, sortField, sortDir]);

  // Deep link from the audit log: ?item=<id> scrolls the row into view and flashes
  // it. Runs once after load; if the item is archived we flip to that tab first so
  // it's actually rendered, then drop the param so a refresh doesn't re-trigger it.
  useEffect(() => {
    if (highlightHydratedRef.current) return;
    if (loading || !highlightParam) return;
    highlightHydratedRef.current = true;

    const target = items.find((i) => i.id === highlightParam);
    if (!target) return;
    if (target.archived && activeTab !== "archived") setParam("tab", "archived");
    setHighlightId(highlightParam);
    setParam("item", null, { replace: true });

    setTimeout(() => {
      // Match on the dataset rather than interpolating the (URL-supplied) id into
      // a selector string — quotes/brackets in the value would throw a
      // DOMException. (CSS.escape isn't an option here: this file imports `CSS`
      // from @dnd-kit/utilities, which shadows the browser CSS global.)
      const rows = Array.from(
        document.querySelectorAll<HTMLElement>("[data-item-id]"),
      );
      const row = rows.find((r) => r.dataset.itemId === highlightParam);
      row?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  }, [loading, highlightParam, items, activeTab, setParam]);

  // Clear the flash a beat after it starts (kept separate so re-renders from the
  // param cleanup above don't cut it short).
  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), 2600);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = filteredItems.findIndex((i) => i.id === active.id);
      const newIndex = filteredItems.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      reorderItems(arrayMove(filteredItems, oldIndex, newIndex));
    },
    [filteredItems, reorderItems]
  );

  const handleAdd = async () => {
    const item = await createItem();
    if (item) setNewItemId(item.id);
  };

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSelected = filteredItems.length > 0 && selectedIds.size === filteredItems.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filteredItems.length;

  const handleToggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filteredItems.map((i) => i.id)));
  };

  const handleTabChange = (tab: Tab) => {
    setParam("tab", tab === "active" ? null : tab);
    setSelectedIds(new Set());
  };

  const handleArchive = async () => {
    await archiveItems([...selectedIds]);
    setSelectedIds(new Set());
  };

  const handleConfirmDelete = async () => {
    await Promise.all([...selectedIds].map((id) => deleteItem(id)));
    setSelectedIds(new Set());
    setShowDeleteConfirm(false);
  };

  const isDraggable = activeTab === "active" && sortField === null;

  return (
    <BoardLayout>
      {error && <ErrorBanner message={error} entityName="action items" />}
      <div className="action-items-page">
        <div className="action-items-tabs">
          <button
            type="button"
            className={`action-items-tab${activeTab === "active" ? " action-items-tab--active" : ""}`}
            onClick={() => handleTabChange("active")}
          >
            Active
          </button>
          <button
            type="button"
            className={`action-items-tab${activeTab === "archived" ? " action-items-tab--active" : ""}`}
            onClick={() => handleTabChange("archived")}
          >
            Archived
          </button>
        </div>
        {loading ? (
          <LoadingState message="Loading action items…" />
        ) : (
          <div className="action-table-wrapper" ref={tableWrapperRef}>
            {/* Non-scrolling header */}
            <table className="action-table">
              {colgroup}
              <thead>
                <tr>
                  <td colSpan={9} className="action-table__toolbar-cell">
                    <div className="action-table-toolbar">
                      <input
                        type="checkbox"
                        className="action-table__select-checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={handleToggleAll}
                        title="Select all"
                      />
                      {activeTab === "active" && (
                        <button
                          type="button"
                          className="action-table-toolbar__btn"
                          disabled={selectedIds.size === 0}
                          onClick={handleArchive}
                          title="Archive selected"
                        >
                          <Archive size={15} />
                          Archive
                        </button>
                      )}
                      <button
                        type="button"
                        className="action-table-toolbar__btn"
                        disabled={selectedIds.size === 0}
                        onClick={() => setShowDeleteConfirm(true)}
                        title="Delete selected"
                      >
                        <Trash2 size={15} />
                        Delete
                      </button>
                      {sortField !== null && (
                        <button
                          type="button"
                          className="action-table-toolbar__btn"
                          onClick={() => setSortField(null)}
                          title="Return to manual order"
                        >
                          Reset sort
                        </button>
                      )}
                      {activeTab === "active" && (
                        <button
                          type="button"
                          className="action-table-toolbar__add-btn"
                          onClick={handleAdd}
                        >
                          <Plus size={15} />
                          Add Item
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr className="action-table__head-row">
                  <th className="action-table__th action-table__th--drag" />
                  <th className="action-table__th action-table__th--select" />
                  <th className="action-table__th action-table__th--date" data-testid="column-header-date">
                    <button
                      type="button"
                      className="action-table__sort-btn"
                      onClick={() => handleSortClick("date")}
                    >
                      Date
                      {sortField === "date" ? (
                        sortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      ) : (
                        <ChevronsUpDown size={13} className="action-table__sort-icon--inactive" />
                      )}
                    </button>
                    {resizeHandle("date", "client")}
                  </th>
                  <th className="action-table__th action-table__th--client" data-testid="column-header-client">
                    Client
                    {resizeHandle("client", "task")}
                  </th>
                  <th className="action-table__th action-table__th--task" data-testid="column-header-task">
                    Task
                    {resizeHandle("task", "owner")}
                  </th>
                  <th className="action-table__th action-table__th--owner" data-testid="column-header-owner">
                    Owner
                    {resizeHandle("owner", "dueDate")}
                  </th>
                  <th className="action-table__th action-table__th--date" data-testid="column-header-dueDate">
                    <button
                      type="button"
                      className="action-table__sort-btn"
                      onClick={() => handleSortClick("due_date")}
                    >
                      Due Date
                      {sortField === "due_date" ? (
                        sortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                      ) : (
                        <ChevronsUpDown size={13} className="action-table__sort-icon--inactive" />
                      )}
                    </button>
                    {resizeHandle("dueDate", "completed")}
                  </th>
                  <th className="action-table__th action-table__th--completed" data-testid="column-header-completed">
                    Completed
                    {resizeHandle("completed", "notes")}
                  </th>
                  <th className="action-table__th action-table__th--notes" data-testid="column-header-notes">Notes</th>
                </tr>
              </thead>
            </table>
            {/* Scrolling body */}
            <div className="action-table-body-scroll">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={filteredItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                  <table className="action-table">
                    {colgroup}
                    <tbody>
                      {filteredItems.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="action-table__empty">
                            {activeTab === "active"
                              ? "No action items yet. Click \"Add Item\" to get started."
                              : "No archived items."}
                          </td>
                        </tr>
                      ) : (
                        filteredItems.map((item) => (
                          <ActionRow
                            key={`${item.id}-${item.updated_at}`}
                            item={item}
                            selected={selectedIds.has(item.id)}
                            onToggleSelect={handleToggleSelect}
                            onUpdate={updateItem}
                            newItemId={newItemId}
                            isDraggable={isDraggable}
                            readOnly={item.archived}
                            highlighted={highlightId === item.id}
                            insertVisible={activeTab === "active" && insertHoverId === item.id}
                            onInsert={async () => {
                              const created = await createItemAfter(item.id);
                              if (created) setNewItemId(created.id);
                              setInsertHoverId(null);
                            }}
                            onMouseEnter={() => showInsertFor(item.id)}
                            onMouseLeave={hideInsert}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </SortableContext>
              </DndContext>
            </div>
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <Modal
          title={`Delete Item${selectedIds.size !== 1 ? "s" : ""}`}
          onClose={() => setShowDeleteConfirm(false)}
          size="small"
        >
          <p>Delete {selectedIds.size} selected item{selectedIds.size !== 1 ? "s" : ""}?</p>
          <div className="modal-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="danger-button"
              onClick={handleConfirmDelete}
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </BoardLayout>
  );
}
